import { AxiosError, AxiosResponse } from "axios";
import dayjs, { Dayjs } from "dayjs";
import { ReactElement, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  AddMeasurementType,
  MeasurementType,
} from "../../../../@types/Measurements";
import { Notification } from "../../../../@types/Notification";
import { IUserPreferences } from "../../../../@types/Preferences";
import { Button } from "../../../../components/Button";
import { DateTimePickerInput } from "../../../../components/DateTimePicker";
import { LinkButton } from "../../../../components/LinkButton";
import { IOption, SelectDropdown } from "../../../../components/SelectDropdown";
import { SkeletonLoading } from "../../../../components/Skeleton";
import { TextField } from "../../../../components/TextField";
import { TimePickerInput } from "../../../../components/TimePicker";
import {
  AUTH_UNIT_PREFERENCES,
  IS_MOBILE_APP,
} from "../../../../constants/localStorageKeys";
import { measurementsService } from "../../../../services/measurementsService";
import { css } from "../../../../styles";
import { isUat } from "../../../../utils/environment";
import { mixpanelActions } from "../../../../utils/mixpanel";
import { getPortalDateFormat } from "../../../../utils/portalDateFormat";
import { safeStorage, safeJsonParse } from "../../../../utils/storage";
import { ErrorMessage } from "../../../PatientList/components/AddPatientModal/AddPatientModal.styles";
import { SubmitContainer } from "../ResolveTriggerModal/ResolveTriggerModal.styles";
import {
  ContainerRow,
  ModalContainer,
  NoteSection,
  SectionContainer,
  Title,
  UnitParagraph,
} from "./AddMeasurementModal.styles";

type AddMeasurementModalProps = {
  section: string;
  patientId: string;
  onClose(notification?: Notification): void;
  measurementType?: string;
};

export function AddMeasurementModal({
  section,
  patientId,
  onClose,
  measurementType,
}: AddMeasurementModalProps) {
  const { t } = useTranslation();

  const [measurementTypes, setMeasurementTypes] = useState<MeasurementType[]>(
    []
  );
  const [updateTypesDropdown, setUpdateTypesDropdown] = useState(false);
  const [responseError, setResponseError] = useState<string>("");
  const [isMobileApp, setIsMobileApp] = useState(false);

  const [category, setCategory] = useState<string | undefined>();
  const [usingDevice, setUsingDevice] = useState("true");
  const [type, setType] = useState<string | undefined>(measurementType);
  const [unit, setUnit] = useState("--");

  const [inputValue, setInputValue] = useState("");
  const [limits, setLimits] = useState<number[]>([]); // [min, max]
  const [bpSystolicValue, setBpSystolicValue] = useState("");
  const [bpDiastolicValue, setBpDiastolicValue] = useState("");

  // EN-5272: the day the measurement was taken, from the portal's date picker (null once cleared).
  const [dateValue, setDateValue] = useState<Dayjs | null>(dayjs());
  const [timeValue, setTimeValue] = useState(dayjs());

  const [categoryOptions, setCategoryOptions] = useState<IOption[]>([]);
  const [typeOptions, setTypeOptions] = useState<IOption[]>([]);

  const usingDeviceOptions: IOption[] = [
    {
      label: t("patientPage_addMeasurement_yes"),
      value: "true",
      key: "using-device-yes-key",
    },
    {
      label: t("patientPage_addMeasurement_no"),
      value: "false",
      key: "using-device-no-key",
    },
  ];

  const inputCss = css({
    span: {
      margin: 0,
    },
  });

  const handleTypeDropdownOptions = (types?: MeasurementType[]) => {
    const typeOptions: IOption[] = [];

    if (types) {
      if (types) {
        types.forEach((type, index) => {
          if (type.category === category) {
            typeOptions.push({
              label: type.measurementTypeDisplayName,
              value: type.measurementType,
              key: `${type.measurementType}-${index}-key`,
            });
          }
        });
      }

      setTypeOptions(typeOptions);
      return;
    }

    if (measurementTypes) {
      measurementTypes.forEach((type, index) => {
        if (type.category === category) {
          typeOptions.push({
            label: type.measurementTypeDisplayName,
            value: type.measurementType,
            key: `${type.measurementType}-${index}-key`,
          });
        }
      });
    }

    setTypeOptions(typeOptions);
  };

  useEffect(() => {
    setType(undefined);
    setUnit("--");
    setInputValue("");
    setBpSystolicValue("");
    setBpDiastolicValue("");
    setUpdateTypesDropdown(false);
    setResponseError("");
  }, [updateTypesDropdown]);

  useEffect(() => {
    if (type) {
      const measurement = measurementTypes.find(
        (x) => x.measurementType === type
      );

      if (!measurement) return;

      setLimits([measurement.min, measurement.max]);

      if (measurement.units.length === 1) {
        if (measurement.units[0]) {
          setUnit(measurement.units[0]);
          return;
        }
      }

      const preferences = safeStorage.getItem(AUTH_UNIT_PREFERENCES);

      const parsedPreferences = safeJsonParse<IUserPreferences | undefined>(
        preferences,
        undefined
      );

      if (parsedPreferences === undefined) {
        if (measurement.units[0]) {
          setUnit(measurement.units[0]);
        }
      } else {
        let unitToUse = "--";

        switch (measurement.measurementType) {
          case "temperature":
            unitToUse =
              measurement.units.find(
                (x) => x === parsedPreferences.temperatureUnit
              ) ?? "--";
            break;
          case "height":
            unitToUse =
              measurement.units.find(
                (x) => x === parsedPreferences.heightUnit
              ) ?? "--";
            break;
          case "weight":
            unitToUse =
              measurement.units.find(
                (x) => x === parsedPreferences.weightUnit
              ) ?? "--";
            break;
          case "pef":
            unitToUse =
              measurement.units.find(
                (x) => x === parsedPreferences.flowRateUnit
              ) ?? "--";
            break;
          case "blood_glucose":
            unitToUse =
              measurement.units.find(
                (x) => x === parsedPreferences.bloodGlucoseUnit
              ) ?? "--";
            break;
          case "phosphate":
            unitToUse =
              measurement.units.find(
                (x) => x === parsedPreferences.phosphateUnit
              ) ?? "--";
            break;
          case "creatinine":
            unitToUse =
              measurement.units.find(
                (x) => x === parsedPreferences.creatinineUnit
              ) ?? "--";
            break;
          case "dyspnea":
            unitToUse = "--";
            break;
          default:
            unitToUse = measurement.units[0] ?? "--";
            break;
        }

        setUnit(unitToUse);
      }
    }
    setResponseError("");
  }, [type]);

  useEffect(() => {
    measurementsService
      .getPatientMeasurementTypes(patientId?.toString())
      .then((result) => {
        const values = result.data.values as unknown;
        const measurementTypes = values as MeasurementType[];

        const newMeasurementTypes = measurementTypes.filter(
          (x) =>
            x.measurementType !== "bp_diastolic" &&
            // EN-5042: hide Breathlessness (mMRC) from the Add menu on UAT only.
            !(isUat() && x.measurementType === "dyspnea")
        );

        const categoryOpts = newMeasurementTypes
          .filter((mt) => mt.category !== "treatments")
          .reduce((categoryOpts: IOption[], measurement) => {
            const { category, categoryDisplayName } = measurement;
            if (!categoryOpts.find((co) => co.value === category)) {
              categoryOpts.push({
                label: categoryDisplayName,
                value: category,
                key: category,
              });
            }

            return categoryOpts;
          }, []);

        setCategoryOptions(categoryOpts);
        setMeasurementTypes(newMeasurementTypes);
        handleTypeDropdownOptions(newMeasurementTypes);

        setCategory(section);
        setType(measurementType);
      });

    const storedIsMobileApp = safeStorage.getItem(IS_MOBILE_APP);

    if (storedIsMobileApp === "true") {
      setIsMobileApp(true);
    }
  }, [section]);

  useEffect(() => {
    handleTypeDropdownOptions();
    setResponseError("");
  }, [category]);

  const handleConfirmAddValue = async () => {
    const parsedValue = parseFloat(inputValue);
    const parsedSytolicBp = parseFloat(bpSystolicValue);
    const parsedDiastolicBp = parseFloat(bpDiastolicValue);

    if (type === "bp_systolic") {
      if (
        (parsedSytolicBp < limits[0] || parsedSytolicBp > limits[1]) &&
        (parsedDiastolicBp < limits[0] || parsedDiastolicBp > limits[1])
      ) {
        setResponseError(
          t("patientPage_addMeasurement_rangeBoth", {
            min: limits[0],
            max: limits[1],
          })
        );
        return;
      }
      if (parsedSytolicBp < limits[0] || parsedSytolicBp > limits[1]) {
        setResponseError(
          t("patientPage_addMeasurement_rangeSystolic", {
            min: limits[0],
            max: limits[1],
          })
        );
        return;
      }

      if (parsedDiastolicBp < limits[0] || parsedDiastolicBp > limits[1]) {
        setResponseError(
          t("patientPage_addMeasurement_rangeDiastolic", {
            min: limits[0],
            max: limits[1],
          })
        );
        return;
      }
    }

    if (parsedValue < limits[0] || parsedValue > limits[1]) {
      setResponseError(
        t("patientPage_addMeasurement_range", {
          min: limits[0],
          max: limits[1],
        })
      );
      return;
    }

    if (dateValue === null) return;

    // The picked day at the picked time, in the user's local time — as before.
    const dateToSend = dateValue
      .hour(timeValue.hour())
      .minute(timeValue.minute())
      .second(0)
      .millisecond(0)
      .toISOString();

    if (!dayjs(dateToSend).isBefore(dayjs())) {
      setResponseError(t("patientPage_addMeasurement_futureTime"));
      return;
    }

    mixpanelActions.track(`User action: Confirm`);

    try {
      let result: AxiosResponse;

      if (type === "bp_systolic") {
        const addMeasurementObj: AddMeasurementType = {
          type: "bp_systolic, bp_diastolic",
          unit: unit === "--" ? "" : unit,
          patientId: parseInt(patientId, 10),
          time: dateToSend,
          locationType: "Clinic",
          bp_systolic: parseFloat(bpSystolicValue),
          bp_diastolic: parseFloat(bpDiastolicValue),
        };

        result = await measurementsService.addPatientMeasurement(
          addMeasurementObj
        );
      } else {
        const addMeasurementObj: AddMeasurementType = {
          type: type!,
          unit: unit === "--" ? "" : unit,
          patientId: parseInt(patientId, 10),
          time: dayjs(
            `${dayjs(dateToSend).format("MM/DD/YYYY")} ${timeValue.format(
              "HH:mm"
            )}`
          ).toISOString(),
          locationType: "Clinic",
          [type!]: parseFloat(inputValue),
        };

        result = await measurementsService.addPatientMeasurement(
          addMeasurementObj
        );
      }

      if (
        result.status !== undefined &&
        result.status > 200 &&
        result.status < 300
      ) {
        onClose({
          show: true,
          message: t("patientPage_addMeasurement_added"),
          type: "success",
          width: "max-content",
          rightMargin: "10%",
        });
      } else {
        setResponseError(t("common_genericError"));
      }
    } catch (error) {
      const axiosError = error as AxiosError<{
        errors?: Array<{ code: string; message: string }>;
      }>;
      const errorMessage =
        axiosError.response?.data?.errors?.[0]?.message ||
        t("common_genericError");
      setResponseError(errorMessage);
    }
  };

  const handleCancel = () => {
    mixpanelActions.track(`User action: Cancel`);
    onClose();
  };

  const handleOnTimeChange = (time: Dayjs | null) => {
    if (time != null) {
      setTimeValue(time);
    } else {
      setTimeValue(dayjs());
    }
  };

  const handleButtonDisabled = () => {
    if (type === "bp_systolic") {
      if (
        bpSystolicValue === "" ||
        bpDiastolicValue === "" ||
        dateValue === null
      ) {
        return true;
      }

      return false;
    }

    if (inputValue === "" || dateValue === null) {
      return true;
    }

    return false;
  };

  const handleInputChange = (event: any) => {
    const { value } = event.target;

    setInputValue(value);
  };

  const handleBpSystolicChange = (event: any) => {
    const { value } = event.target;

    setBpSystolicValue(value);
  };

  const handleBpDiastolicChange = (event: any) => {
    const { value } = event.target;

    setBpDiastolicValue(value);
  };

  const handleModalRows = () => {
    const rowsToDisplay: ReactElement[] = [];

    if (isMobileApp) {
      rowsToDisplay.push(
        <ContainerRow>
          <p>{t("patientPage_addMeasurement_usingDevice")}</p>
          <SelectDropdown
            value={usingDevice}
            options={usingDeviceOptions}
            placeholder={t("patientPage_addMeasurement_selectOption")}
            width={191.5}
            onValueChange={(usingDevice) => {
              setUsingDevice(usingDevice.toString());
            }}
          />
        </ContainerRow>
      );
    }

    if (usingDevice === "false" || !isMobileApp) {
      rowsToDisplay.push(
        <>
          <ContainerRow>
            <p>{t("patientPage_addMeasurement_type")}</p>
            {!updateTypesDropdown ? (
              <SelectDropdown
                value={type}
                options={typeOptions}
                placeholder={t("patientPage_addMeasurement_selectType")}
                width={191.5}
                onValueChange={(type) => {
                  mixpanelActions.track(`User action: Select ${type}`);
                  setType(type.toString());
                  setInputValue("");
                  setBpSystolicValue("");
                  setBpDiastolicValue("");
                  setResponseError("");
                }}
              />
            ) : null}
          </ContainerRow>
          <ContainerRow disabled={!type}>
            <p>{t("patientPage_addMeasurement_time")}</p>
            <TimePickerInput value={timeValue} onChange={handleOnTimeChange} />
          </ContainerRow>
          <ContainerRow disabled={!type}>
            <p>{t("patientPage_addMeasurement_startDate")}</p>
            <DateTimePickerInput
              showTime={false}
              format={getPortalDateFormat()}
              placeholder={getPortalDateFormat()}
              // The TextField width of the value field below it.
              width="200px"
              maxDate={dayjs()}
              value={dateValue}
              // Clearing hands back null despite the signature.
              onChange={(next: Dayjs | null) => setDateValue(next)}
            />
          </ContainerRow>
          <ContainerRow disabled={!type}>
            <p>{t("patientPage_addMeasurement_unit")}</p>
            <UnitParagraph>{unit}</UnitParagraph>
          </ContainerRow>
          {type !== "bp_systolic" ? (
            <ContainerRow disabled={!type}>
              <p>{t("patientPage_addMeasurement_value")}</p>
              <TextField
                label=""
                placeholder="0"
                type="number"
                backgroudColor="white"
                value={inputValue}
                className={inputCss()}
                onChange={handleInputChange}
              />
            </ContainerRow>
          ) : (
            <>
              <ContainerRow>
                <p>{t("patientPage_addMeasurement_systolic")}</p>
                <TextField
                  label=""
                  placeholder="0"
                  type="number"
                  backgroudColor="white"
                  value={bpSystolicValue}
                  className={inputCss()}
                  onChange={handleBpSystolicChange}
                />
              </ContainerRow>
              <ContainerRow>
                <p>{t("patientPage_addMeasurement_diastolic")}</p>
                <TextField
                  label=""
                  placeholder="0"
                  type="number"
                  backgroudColor="white"
                  value={bpDiastolicValue}
                  className={inputCss()}
                  onChange={handleBpDiastolicChange}
                />
              </ContainerRow>
            </>
          )}
        </>
      );
    }

    return rowsToDisplay;
  };

  const handleOpenApp = () => {
    (window as { [key: string]: any }).sendMessageToFlutter(String(patientId));
  };

  return (
    <ModalContainer>
      <Title>
        {t("patientPage_addMeasurement_title", {
          category:
            category !== "lab_result"
              ? category
              : t("patientPage_addMeasurement_lab"),
        })}
        {!category && <SkeletonLoading width="100px" />}
      </Title>

      <SectionContainer>
        <ContainerRow>
          <p>{t("patientPage_addMeasurement_category")}</p>
          <SelectDropdown
            value={category}
            options={categoryOptions}
            placeholder={t("patientPage_addMeasurement_selectCategory")}
            width={191.5}
            onValueChange={(category) => {
              setCategory(category.toString());
              setUpdateTypesDropdown(true);
            }}
          />
        </ContainerRow>

        {handleModalRows()}
      </SectionContainer>

      <ErrorMessage
        style={{ position: "relative", left: "0px", marginBottom: "16px" }}
      >
        {responseError}
      </ErrorMessage>

      {usingDevice === "true" && isMobileApp ? (
        <NoteSection>
          <p style={{ fontWeight: "600" }}>
            {t("patientPage_addMeasurement_noteTitle")}
          </p>
          <p>{t("patientPage_addMeasurement_noteBody")}</p>
        </NoteSection>
      ) : null}

      <SubmitContainer noPadding>
        {usingDevice === "true" && isMobileApp ? (
          <Button
            label={t("patientPage_addMeasurement_openApp")}
            type="button"
            onClick={handleOpenApp}
          />
        ) : (
          <Button
            label={t("patientPage_addMeasurement_confirm")}
            type="button"
            onClick={handleConfirmAddValue}
            disabled={handleButtonDisabled()}
          />
        )}
        <LinkButton
          label={t("common_cancel")}
          size="lg"
          color="black"
          fontWeight="semiBold"
          onClick={handleCancel}
        />
      </SubmitContainer>
    </ModalContainer>
  );
}
